


































import { Component, Mixins, Watch } from "vue-property-decorator";
import { FuzzyDate } from "../../../../domain/system";
import { IItem, DateItem, instanceOfITextCopyable, instanceOfIHisInformationSelectable } from "../../../../domain/document/items";
import { DateInputMethodKinds } from "../../../../domain/document/inputMethodKinds";
import { DateInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import { IHisInformationGroupSource } from "../../../../domain/his/groupInput/HisInformationGroupSource";
import { instanceOfISingleSelectableConditionallyAsynchronously } from "../../../system";
import {
  IConfiguration,
} from "../../../../domain/configuration";
import { IObjectStyleReadable } from "../IObjectStyleReadable";
import {
  CommonComponentsAccessibleMixin,
} from "../mixins";
import { DateObjectStyleReader } from "../styleReaders";
import {
  StyledTextEditableObjectMixin,
  HisInformationSelectableMixin,
  UserDefinitionSelectableMixin,
} from "./mixins";
import { IFuzzyDateSelectCondition } from "./dialogs";
import TextInputArea from "./TextInputArea.vue";
import { IObject } from "./IObject";
import { IItemPastableObject } from "./IItemPastableObject";
import { IItemCopyableObject } from "./IItemCopyableObject";
import { IFreeInputableObject } from "./IFreeInputableObject";
import { IDcoumentDataDroppableObject } from "./IDcoumentDataDroppableObject";
import { IDocumentDataDragParameter } from "../../../dragging";

@Component({
  components: {
    TextInputArea,
  }
})
export default class DateObject extends Mixins(
  StyledTextEditableObjectMixin,
  HisInformationSelectableMixin,
  UserDefinitionSelectableMixin,
  CommonComponentsAccessibleMixin,
) implements
  IObject,
  IItemPastableObject,
  IItemCopyableObject,
  IFreeInputableObject,
  IDcoumentDataDroppableObject {
  get editStyleReader(): IObjectStyleReadable {
    const configuration = this.$store.getters.configuration as IConfiguration;
    return new DateObjectStyleReader(configuration.style.document.editor.objects.date);
  }

  editingSource: DateItem;

  updateInputElement(value: HTMLInputElement): void {
    this.inputableElement = value;
  }

  @Watch("isFocused")
  updateIsFocused(newValue: boolean, oldValue: boolean): void {
    if (!newValue) {
      this.adjustTextFontSize();
    }
  }

  get displayText(): string {
    this.updateTextPositionHeight();
    if (this.editingSource.isEmpty) {
      return this.alternativeText;
    }
    return this.editingSource.text();
  }

  set displayText(value: string) {
    this.sourceValue = new FuzzyDate(value, this.sourceValue.isTime);
  }

  get freeInputText(): string {
    return this.displayText;
  }

  set freeInputText(value: string) {
    this.displayText = value;
  }

  get inputMethodKind(): DateInputMethodKinds {
    return this.editingSource.inputMethodKind;
  }

  set inputMethodKind(value: DateInputMethodKinds) {
    this.writeProperty(s => {
      s.inputMethodKind = value;
      if (value !== DateInputMethodKinds.HisInfoSelect && instanceOfIHisInformationSelectable(s)) {
        s.setSelectedItems(null);
      }
    });
  }

  @Watch("inputMethodKind")
  updateInputMethodKind(newValue: DateInputMethodKinds, oldValue: DateInputMethodKinds): void {
    if (this.isInputMethodKindLastUpdatedExternally &&
      newValue !== DateInputMethodKinds.HisInfoSelect &&
      oldValue !== DateInputMethodKinds.Calendar) {
      this.showInputDialog();
    }
  }

  get isInputMethodKindLastUpdatedExternally(): boolean {
    return this.editingSource.isInputMethodKindLastUpdatedExternally;
  }

  get inputMethodOptionKind(): DateInputMethodOptionKinds {
    return this.editingSource.inputMethodOptionKind;
  }

  get title(): string {
    return this.toolTip
      ? this.toolTip
      : this.objectName;
  }

  get objectName(): string {
    if (this.inputMethodOptionKind === DateInputMethodOptionKinds.UserDefinition) { // ユーザ定義
      return this.editingSource.itemName;
    }
    if (this.editingSource.isHisInfo) { // HIS項目
      return this.editingSource.displayName;
    }
    return this.editingSource.name;
  }

  get isCalendarInputtable(): boolean {
    return this.editingSource.isCalendarInputtable && !this.isEditDisabledByRuleSet;
  }

  get isHisInfoSelectInputable(): boolean {
    return this.editingSource.isHisInfoSelectInputable && !this.isEditDisabledByRuleSet;
  }

  get isApproximateInputable(): boolean {
    return this.editingSource.enableApproximateInput && !this.isEditDisabledByRuleSet;
  }

  onFocus(): void {
    // フリー入力時、dateFocusObjectにフォーカスを当てるとフリー入力オブジェクトのフォーカスを奪い入力出来なくなる場合がある
    if (!this.shouldInputFreeSelfOrAll) {
      (this.$refs.dateFocusObject as HTMLElement).focus();
    }
    this.emitFocus();
  }

  showInputDialog(): void {
    if (this.shouldInputFreeSelfOrAll || this.isEditDisabledByRuleSet) { return; }
    
    switch (this.inputMethodKind) {
      case DateInputMethodKinds.Calendar: this.showCalendarDialog(); break;
      case DateInputMethodKinds.HisInfoSelect: this.showHisInfoSelectDialog(); break;
      case DateInputMethodKinds.UserDefinitionSelect: this.showUserDefinitionSelectDialog(); break;
    }
  }

  /** 値が空のときの代替テキスト */
  private get alternativeText(): string {
    if (!this.shouldInputFreeSelfOrAll && this.inputMethodKind !== DateInputMethodKinds.Calendar) {
      switch (this.inputMethodOptionKind) {
        case DateInputMethodOptionKinds.Normal:
        case DateInputMethodOptionKinds.Link:
          return this.editingSource.text();
        case DateInputMethodOptionKinds.Calculate:
          return this.$configuration.wording.document.editor.displayTextForCalculationError;
        case DateInputMethodOptionKinds.HisItem:
        case DateInputMethodOptionKinds.HisInfoSelect: // HIS情報選択とユーザ定義は値が空のとき項目名を表示
        case DateInputMethodOptionKinds.UserDefinition:
        case DateInputMethodOptionKinds.HisFreeInput:
          return this.objectName;
      }
    }
    return this.editingSource.text();
  }

  private get sourceValue(): FuzzyDate {
    return this.editingSource.value;
  }

  private set sourceValue(value: FuzzyDate) {
    this.writeProperty((s: DateItem) => {
      if (this.shouldInputFreeSelfOrAll && this.isFreeInputable) {
        // 「全てフリー入力」でフリー入力になっている状態で手入力されたときはフリー入力にする
        s.shouldInputFree = true;
      }
      s.value = value;
      s.adjustFontSize();
    });
  }

  private showCalendarDialog(): void {
    if (!this.isCalendarInputtable) { return; }

    const dialog = this.sourceValue.isTime
      ? this.commonComponents.dialogs.time.fuzzySelector
      : this.commonComponents.dialogs.date.fuzzySelector;
    if (instanceOfISingleSelectableConditionallyAsynchronously<FuzzyDate, IFuzzyDateSelectCondition>(dialog)) {
      dialog.selectOptionConditionally(this.conditionToSelect, this.sourceValue.clone()).then(result => {
        if (!result) { return; }
        this.sourceValue = result;
        this.onFocus();
      });
    }
  }

  private get conditionToSelect(): IFuzzyDateSelectCondition {
    return {
      title: this.objectName,
      format: this.editingSource.format,
      enableApproximateInput: this.editingSource.enableApproximateInput,
    };
  }

  private showHisInfoSelectDialog(): void {
    this.selectHisInformation(r => this.onHisItemSelected(r));
  }
  
  async onHisItemSelected(result: IHisInformationGroupSource): Promise<boolean> {
    if (!result) {
      this.inputMethodKind = DateInputMethodKinds.Calendar;
    }
    return true;  // 処理継続可否は任せる.
  }

  private showUserDefinitionSelectDialog(): void {
    this.selectUserDefinition();
  }

  adjustTextFontSize(): void {
    this.autoAdjustFontSize();
  }

  pasteText(value: string): void {
    this.insertTextToCaretPosition(value);
    this.$nextTick(() => {
      this.adjustTextFontSize();
    });
  }

  overwriteText(value: string, isForcibly?: boolean): void {
    if (isForcibly) {
      this._overwriteTextForcibly(value);
    } else {
      this.overwriteText(value);
    }
  }

  async overwriteDate(value: Date | string): Promise<void> {
    if (!this.isCalendarInputtable) {
      this.$logger.warn("カレンダー入力不可なので強制的に反映できません.");
      return;
    }
    this.writeProperty(s => {
      s.setFixedDate(value);
    });
    this.$nextTick(() => {
      this.adjustTextFontSize();
    });
  }

  private async _overwriteTextForcibly(value: string): Promise<void> {
    if (!await this._setInputFree()) { return; }
    await this._overwriteText(value);
  }

  private async _setInputFree(): Promise<boolean> {
    if (!this.isFreeInputable) {
      this.$logger.warn("フリー入力不可なので強制的に反映できません.");
      return false;
    }
    if (this.inputMethodKind !== DateInputMethodKinds.FreeInput) {
      this.$logger.info("フリー入力じゃないので強制的にフリー入力に変更して反映します.");
      this.inputMethodKind = DateInputMethodKinds.FreeInput;
      await this.$nextTick();
    }
    return true;
  }

  private async _overwriteText(value: string): Promise<void> {
    this.freeInputText = value;
    await this.$nextTick();
    this.adjustTextFontSize();
  }

  get isSourceEmpty(): boolean {
    return !this.editingSource
        || !this.editingSource.value
        || !this.editingSource.value.value;
  }

  isItemPastable(source: IItem): boolean {
    if (this.isEditDisabledByRuleSet) {
      return false;
    }
    return this.isPastableDateItem(source)
        || (this.shouldInputFreeSelfOrAll && instanceOfITextCopyable(source));
  }

  private isPastableDateItem(source: IItem): boolean {
    // V1では日付コピーはフリー入力可能な日付オブジェクトのみ貼り付け可能になっている
    // ただ、カレンダー入力可能なオブジェクトに日付が貼り付くのは問題ないはずなので、そこはV3では許可するようにしている
    // source.value.isFuzzyの判定だと頃・中旬などの曖昧日付には対応できないが、
    // そもそもカレンダーのみの入力指定しているオブジェクトに曖昧日付を貼り付けできなくて問題になることは無い見込み
    return this.editingSource
        && (this.inputMethodKind === DateInputMethodKinds.Calendar) // TODO: V1はデータ選択のときも日付貼り付けできる模様
        && (source instanceof DateItem)
        && (source.value instanceof FuzzyDate)
        && !!source.value.value
        && (this.editingSource.value.isTime === source.value.isTime)
        && (this.isFreeInputable || (this.isCalendarInputtable && !source.value.isFuzzy));
  }

  pasteItem(source: IItem): void {
    if (this.isPastableDateItem(source)) {
      this.writePropertyWhenValid(s => s.value = (source as DateItem).value.clone());
      this.$nextTick(() => {
        this.adjustTextFontSize();
      });
    } else if (this.shouldInputFreeSelfOrAll && instanceOfITextCopyable(source)) {
      this.pasteText(source.textToCopy);
      this.$nextTick(() => {
        this.adjustTextFontSize();
      });
    }
  }

  get textPaintStyleProperties() {
    return {
      backgroundColor: "transparent",
    };
  }

  setDroppedDocumentData(text: string, param: IDocumentDataDragParameter): void {
    this.writeProperty((s: DateItem) => {
      s.setDroppedDocumentData(text, param);
    });
    this.$nextTick(() => {
      this.adjustTextFontSize();
    });
  }
}
